export { LicenseSnippet }

import { useState } from 'react'
import type { LicenseMap } from './wire.js'
import { renderLicenseSnippetLines } from './snippet.js'
import { renderTokens } from './renderTokens.js'

// The license-snippet code block — the SINGLE rendering shared by the dashboard's `LicenseKeysPanel`
// and license-components' `FreeTrialForm`, so the two surfaces are byte-identical (symlinked into
// `packages/license-components/src/` alongside snippet.ts / wire.ts / renderTokens.tsx).
//
// Rendered with plain <div>s, NOT <pre><code>, on purpose: vike.dev's docpress force-styles the
// `pre` and `code` *elements* through cascade layers that un-layered `!important` can't beat — `code`
// gets display:block + 16px padding on every side, and `pre` gets a 13px font-size. That capped the
// scrollable width at the viewport (clipping the diff highlight ~32px short of `code`'s edge) and
// pinned the font so it wouldn't inherit. Generic <div>s the host's selectors don't target dodge all
// of it: we own the padding (so every `.line`'s green spans the full SCROLL width) and the font-size
// (so it inherits the host prose) — identically on the dashboard and on vike.dev. CSS is injected as
// a scoped <style> (everything under `.license-snippet`) rather than a CSS import, because
// license-components is compiled by `tsc` (no CSS-import support); Vite (the dashboard) is fine with
// it too. `// pages/+config.js` + a blank line lead the block (context lines: `license: false`, not
// diff-highlighted, excluded from Copy) — mirrors vike.dev/license.
function LicenseSnippet({ license }: { license: LicenseMap }) {
  const [copied, setCopied] = useState(false)
  const lines = [
    { text: '// pages/+config.js', license: false },
    { text: '', license: false },
    ...renderLicenseSnippetLines(license),
  ]
  // Copy only the `license: {…}` lines (not the filename comment or `export default {}` wrapper), so
  // a customer with an existing +config pastes just the key — matches the diff-highlighted lines.
  async function copy() {
    try {
      await navigator.clipboard.writeText(
        lines
          .filter((l) => l.license)
          .map((l) => l.text)
          .join('\n'),
      )
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard can reject (denied permission / insecure context) — leave the button unchanged.
    }
  }
  return (
    <div style={snippetWrapStyle}>
      <style>{SNIPPET_CSS}</style>
      <div className="license-snippet">
        {/* inner shrink-wrap element (see SNIPPET_CSS) — carries the block's padding + monospace */}
        <div className="license-snippet-code">
          {lines.map((line, i) => (
            <div key={i} className={`line${line.license ? ' add' : ''}${line.text.startsWith('//') ? ' comment' : ''}`}>
              {line.text === '' ? ' ' : renderTokens(line.text)}
            </div>
          ))}
        </div>
      </div>
      <button type="button" onClick={copy} style={copyButtonStyle}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

const snippetWrapStyle = { position: 'relative' as const, margin: '8px 0' }
const copyButtonStyle = {
  position: 'absolute' as const,
  top: 6,
  right: 6,
  padding: '4px 8px',
  fontSize: 12,
  background: '#fff',
  border: '1px solid #ddd',
  borderRadius: 4,
  cursor: 'pointer',
  color: '#333',
}
// github-light look (docpress's Shiki theme) without the dependency. `background:#f4f4f4` is solid
// (not docpress's rgba-on-white) so the block reads as a light code surface — dark text on light —
// on any host box tint or theme. `.license-snippet` is the scroll viewport; the inner
// `.license-snippet-code` (`inline-block; min-width:100%`) shrink-wraps to the widest line so every
// `.line` (a block child) is exactly as wide, and its diff background spans the full SCROLL width
// (no ~32px right-edge gap, which a host-padded `<code>` produced). font-size is a fixed 13px to
// match vike.dev's other (docpress) code blocks (not the 16px prose), and the 1px border keeps the
// block distinct from a tinted container (e.g. FreeTrialForm's gray result box) — both possible only
// because these are <div>s, not docpress-styled `pre`/`code`.
const SNIPPET_CSS = `
.license-snippet { margin: 0; border: 1px solid #e2e4e8; border-radius: 6px; overflow-x: auto; background: #f4f4f4; font-size: 13px; line-height: 1.6; }
.license-snippet .license-snippet-code { display: inline-block; min-width: 100%; box-sizing: border-box; padding: 16px 0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace; color: #24292e; }
.license-snippet .line { display: block; position: relative; padding: 0 16px; white-space: pre; }
.license-snippet .line.add { background: #d7ecdf; }
.license-snippet .line.add::before { content: '+'; position: absolute; top: -1px; left: 4px; color: #89d189; font-size: 1.2em; font-weight: 500; }
.license-snippet .line.comment { color: #6a737d; }
.license-snippet .tok-kw { color: #d73a49; }
.license-snippet .tok-str { color: #032f62; }
`
